/** Client preview mirror of the server promotion engine; the authoritative charge stays server-side. */

/** A promotion authored in the feature-flag payload. Carries exactly one of `newBasePrice` / `discountPct`. */
export interface ModelPromotion {
  modelName: string;
  /** Restricts the promotion to price rows carrying this `modelVariant`; omit to cover every variant. */
  modelVariant?: string;
  newBasePrice?: number;
  discountPct?: number;
  beginsAt?: string;
  expiresAt?: string;
}

/** The promotion selected for one model, resolved to a concrete base credit value. */
export interface ResolvedPromotion {
  newBasePrice: number;
  expiresAt?: string;
}

const isValidOptionalDate = (value: unknown): boolean =>
  value === undefined || (typeof value === 'string' && !Number.isNaN(Date.parse(value)));

const isValidPromotion = (entry: unknown): entry is ModelPromotion => {
  if (typeof entry !== 'object' || entry === null) {
    return false;
  }
  const { modelName, modelVariant, newBasePrice, discountPct, beginsAt, expiresAt } = entry as Record<string, unknown>;
  if (typeof modelName !== 'string' || modelName.trim() === '') {
    return false;
  }
  if (modelVariant !== undefined && (typeof modelVariant !== 'string' || modelVariant.trim() === '')) {
    return false;
  }
  const hasAbsolute = typeof newBasePrice === 'number';
  const hasPct = typeof discountPct === 'number';
  if (hasAbsolute === hasPct) {
    return false;
  }
  if (hasAbsolute && (!Number.isFinite(newBasePrice) || newBasePrice < 0)) {
    return false;
  }
  if (hasPct && (!Number.isFinite(discountPct) || discountPct <= 0 || discountPct > 100)) {
    return false;
  }
  if (!isValidOptionalDate(beginsAt) || !isValidOptionalDate(expiresAt)) {
    return false;
  }
  return true;
};

const isWithinWindow = (promo: ModelPromotion, now: Date): boolean =>
  (promo.beginsAt === undefined || Date.parse(promo.beginsAt) <= now.getTime()) &&
  (promo.expiresAt === undefined || Date.parse(promo.expiresAt) > now.getTime());

/** Parses and validates a feature-flag payload into the promotions active at `now`. */
export const parseActivePromotions = (payload: unknown, now: Date = new Date()): ModelPromotion[] => {
  const rawList = Array.isArray(payload)
    ? payload
    : typeof payload === 'object' && payload !== null && Array.isArray((payload as Record<string, unknown>).promotions)
      ? ((payload as Record<string, unknown>).promotions as unknown[])
      : [];
  const active: ModelPromotion[] = [];
  for (const entry of rawList) {
    if (isValidPromotion(entry) && isWithinWindow(entry, now)) {
      active.push({
        modelName: entry.modelName,
        modelVariant: entry.modelVariant,
        newBasePrice: entry.newBasePrice,
        discountPct: entry.discountPct,
        beginsAt: entry.beginsAt,
        expiresAt: entry.expiresAt,
      });
    }
  }
  return active;
};

const effectiveBase = (promo: ModelPromotion, baseCredits: number): number =>
  typeof promo.newBasePrice === 'number' ? promo.newBasePrice : baseCredits * (1 - (promo.discountPct as number) / 100);

/** Selects the lowest-priced promotion for `priceName`; undefined when none applies or none lowers the price. */
export const resolvePromotionForModel = (
  promotions: ModelPromotion[],
  priceName: string,
  baseCredits: number,
  modelVariant?: string | null,
): ResolvedPromotion | undefined => {
  const target = priceName.toLowerCase();
  const targetVariant = modelVariant?.toLowerCase() ?? null;
  const resolved = promotions
    .filter((promo) => promo.modelName.toLowerCase() === target)
    .filter((promo) => promo.modelVariant === undefined || promo.modelVariant.toLowerCase() === targetVariant)
    .map((promo) => ({ newBasePrice: effectiveBase(promo, baseCredits), expiresAt: promo.expiresAt }));
  if (resolved.length === 0) {
    return undefined;
  }
  const best = resolved.reduce((lowest, promo) => (promo.newBasePrice < lowest.newBasePrice ? promo : lowest));
  return best.newBasePrice < baseCredits ? best : undefined;
};
